import React, { useMemo, useState } from "react";
import { FormInput } from "@k8slens/lds-form";
import { ButtonBar } from "@k8slens/lds";
import { BusinessSsoOidcDto, BusinessSsoSamlDto, SSOType } from "lens-platform-sdk";
import { Modal } from "@k8slens/lds";

import TrackedButton from "src/components/Button/TrackedButton";

import styles from "./OIDCSSOProviderForm.module.css";
import { misc } from "@k8slens/lds-icons";
import Notification from "../Notification/Notification";
import Button from "src/components/Button/TrackedButton";

import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";

const { AwesomeIcon } = misc;

interface SSOFormProps {
  onSubmit: (settings: BusinessSsoSamlDto | BusinessSsoOidcDto) => void;
  loading: boolean;
}

const dataFieldDefaults = {
  authorizationUrl: { value: "", valid: false },
  tokenUrl: { value: "", valid: false },
  jwksUrl: { value: "", valid: false },
  userInfoUrl: { value: "", valid: false },
  issuer: { value: "", valid: false },
  logoutUrl: { value: "", valid: false },
  clientSecret: { value: "", valid: false },
  clientId: { value: "", valid: false },
  oidcDiscoveryUrl: { value: "", valid: false },
};

export const OIDCSSOProviderForm = ({ onSubmit, loading }: SSOFormProps) => {
  const [authorizationUrl, setAuthorizationUrl] = useState(dataFieldDefaults.authorizationUrl);
  const [tokenUrl, setTokenUrl] = useState(dataFieldDefaults.tokenUrl);
  const [jwksUrl, setJwksUrl] = useState(dataFieldDefaults.jwksUrl);
  const [userInfoUrl, setUserInfoUrl] = useState(dataFieldDefaults.userInfoUrl);
  const [logoutUrl, setLogoutUrl] = useState(dataFieldDefaults.logoutUrl);
  const [issuer, setIssuer] = useState(dataFieldDefaults.issuer);
  const [clientSecret, setClientSecret] = useState(dataFieldDefaults.clientSecret);
  const [clientId, setClientId] = useState(dataFieldDefaults.clientId);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [oidcDiscoveryUrl, setOidcDiscoveryUrl] = useState(dataFieldDefaults.oidcDiscoveryUrl);
  const lensPlatformClient = useLensPlatformClient();

  const onCancel = () => {
    setImportModalOpen(false);
  };

  const valid = useMemo(
    () => [authorizationUrl, tokenUrl, clientId, clientSecret].every(({ valid }) => valid !== false),
    [authorizationUrl, tokenUrl, clientId, clientSecret],
  );

  const handleImport = async () => {
    if (oidcDiscoveryUrl.value && oidcDiscoveryUrl.valid) {
      setError(null);

      try {
        const configRequestResult = await lensPlatformClient.sso.getRemoteOIODCConfiguration(oidcDiscoveryUrl.value);

        setJwksUrl({ value: configRequestResult.jwksUrl, valid: true });
        setAuthorizationUrl({
          value: configRequestResult.authorizationUrl,
          valid: true,
        });
        setIssuer({ value: configRequestResult.issuer, valid: true });
        setLogoutUrl({ value: configRequestResult.logoutUrl, valid: true });
        setUserInfoUrl({ value: configRequestResult.userInfoUrl, valid: true });
        setTokenUrl({ value: configRequestResult.tokenUrl, valid: true });

        setError(null);
        setOidcDiscoveryUrl({ value: "", valid: false });
        setImportModalOpen(false);
      } catch (error) {
        setError(new Error("Error importing OIDC configuration"));
        setImportModalOpen(false);
        setOidcDiscoveryUrl({ value: "", valid: false });
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    onSubmit({
      authorizationUrl: authorizationUrl.value,
      tokenUrl: tokenUrl.value,
      jwksUrl: jwksUrl.value,
      userInfoUrl: userInfoUrl.value,
      logoutUrl: logoutUrl.value,
      issuer: issuer.value,
      clientSecret: clientSecret.value,
      clientId: clientId.value,
      type: SSOType.OIDC,
    });
  };

  return (
    <form aria-label="OIDC SSO Provider settings" autoComplete="on" onSubmit={handleSubmit}>
      {error && (
        <Notification
          className={styles.error}
          level="error"
          message={error.message}
          type="flash"
          onClose={() => setError(null)}
        />
      )}
      <fieldset className={styles.fieldset}>
        <div className={styles.gridForm}>
          <FormInput
            className={styles.input}
            label="Client id"
            name="clientId"
            value={clientId.value}
            onChange={(value, valid) => setClientId({ value, valid })}
            required
          />
          <FormInput
            className={styles.input}
            label="Client secret"
            name="clientSecret"
            value={clientSecret.value}
            onChange={(value, valid) => setClientSecret({ value, valid })}
            required
          />
          <FormInput
            label="Authorization Url"
            type="url"
            name="singleSignOnServiceUrl"
            placeholder="https://login.xyz.com/id/oauth2/v2.0/authorize"
            value={authorizationUrl.value}
            onChange={(value, valid) => setAuthorizationUrl({ value, valid })}
            required
            className={styles.input}
          />
          <div className={styles.import}>
            <TrackedButton
              discreet
              loading={loading}
              label="Import configuration"
              type="ok"
              icon={AwesomeIcon}
              onClick={() => setImportModalOpen(true)}
              textTransform="capitalize"
            />
          </div>
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="Token URL"
            type="url"
            name="tokenUrl"
            placeholder="https://login.xyz.com/id/oauth2/v2.0/token"
            value={tokenUrl.value}
            onChange={(value, valid) => setTokenUrl({ value, valid })}
            required
          />
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="JWKS URL"
            type="url"
            name="jwksUrl"
            placeholder="https://login.xyz.com/id/discovery/v2.0/keys"
            value={jwksUrl.value}
            onChange={(value, valid) => setJwksUrl({ value, valid })}
            required
          />
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="User Info URL"
            type="url"
            name="userInfoUrl"
            placeholder="https://graph.xyz.com/oidc/userinfo"
            value={userInfoUrl.value}
            onChange={(value, valid) => setUserInfoUrl({ value, valid })}
            required
          />
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="Logout URL"
            type="url"
            name="logoutUrl"
            placeholder="https://login.xyz.com/id/oauth2/v2.0/logout"
            value={logoutUrl.value}
            onChange={(value, valid) => setLogoutUrl({ value, valid })}
            required
          />
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="Issuer"
            type="url"
            name="issuer"
            placeholder="https://login.xyz.com/id/v2.0"
            value={issuer.value}
            onChange={(value, valid) => setIssuer({ value, valid })}
            required
          />
        </div>
        <ButtonBar type="grid" className={styles.buttonBar}>
          <TrackedButton
            label="Save single sign-on settings"
            onClick={handleSubmit}
            primary
            disabled={!valid}
            loading={loading}
            textTransform="capitalize"
          />
        </ButtonBar>
      </fieldset>
      {importModalOpen && (
        <Modal
          title="Import from openid configuration"
          size="md"
          onClose={onCancel}
          isOpen={importModalOpen}
          footer={
            <>
              <Button label="Cancel" data-testId="cancel-import" onClick={onCancel} />
              <Button
                label="Import"
                data-testId="Import"
                disabled={!oidcDiscoveryUrl.value}
                onClick={handleImport}
                primary
              />
            </>
          }
        >
          <p>Use openid configuration URL to auto-populate the OIDC fields.</p>
          <FormInput
            wrapperProps={{ className: styles.tokenUrl }}
            label="OIDC configuration discovery URL"
            type="url"
            name="oidcDiscoveryUrl"
            placeholder="https:/xyz.com/id/v2.0/.well-known/openid-configuration"
            value={oidcDiscoveryUrl.value}
            onChange={(value, valid) => setOidcDiscoveryUrl({ value, valid })}
          />
        </Modal>
      )}
    </form>
  );
};
